import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, AlertTriangle, Play, Plus, Trash2 } from 'lucide-react';
import { SimulationParameters, YearlyReturn, XFactorEvent, ModelType, InvestmentStrategy, VolatilityLevel } from './types';
interface SimulationInputPanelProps {
  onRunSimulation: (params: SimulationParameters) => void;
  isLoading: boolean;
}
export function SimulationInputPanel({
  onRunSimulation,
  isLoading
}: SimulationInputPanelProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [parameters, setParameters] = useState<SimulationParameters>({
    initialInvestment: 10000,
    initialSharePrice: 100,
    numberOfShares: 100,
    timeHorizon: 10,
    yearlyReturns: Array.from({
      length: 10
    }, (_, i) => ({
      year: i + 1,
      return: 8
    })),
    volatilityLevel: 'medium',
    customVolatility: 20,
    weeklyDCAAmount: 100,
    xFactorEvents: [],
    investmentStrategy: 'hold',
    modelType: 'simple',
    numberOfSimulations: 1000
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target as HTMLInputElement;
    if (type === 'number' || type === 'range') {
      setParameters({
        ...parameters,
        [name]: parseFloat(value)
      });
    } else {
      setParameters({
        ...parameters,
        [name]: value
      });
    }
    // Special case for share price and investment amount
    if (name === 'initialInvestment' || name === 'initialSharePrice') {
      const investment = name === 'initialInvestment' ? parseFloat(value) : parameters.initialInvestment;
      const price = name === 'initialSharePrice' ? parseFloat(value) : parameters.initialSharePrice;
      if (price > 0) {
        setParameters(prev => ({
          ...prev,
          numberOfShares: Math.floor(investment / price)
        }));
      }
    }
  };
  const handleYearlyReturnChange = (year: number, value: number) => {
    setParameters(prev => ({
      ...prev,
      yearlyReturns: prev.yearlyReturns.map(yr => yr.year === year ? {
        ...yr,
        return: value
      } : yr)
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRunSimulation(parameters);
  };
  // Simplification: Hardcode volatility to medium (20%) hidden from user
  const getVolatilityValue = (): number => 20;

  return (
    <div className="bg-theme-secondary rounded-xl shadow-lg border border-theme-tertiary p-6 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-500/10 rounded-lg">
          <Play className="text-blue-500" size={20} fill="currentColor" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-theme-primary">Simulation Setup</h2>
          <p className="text-xs text-theme-secondary">Define your investment strategy</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
        <div className="space-y-5">
          {/* Investment Amount */}
          <div>
            <label className="block text-sm font-semibold text-theme-secondary mb-1.5">
              Initial Investment ($)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-secondary">$</span>
              <input
                type="number"
                name="initialInvestment"
                value={parameters.initialInvestment}
                onChange={handleInputChange}
                min="1"
                className="w-full pl-8 pr-4 py-3 bg-theme-tertiary/50 border border-theme-tertiary rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium transition-all"
              />
            </div>
          </div>

          {/* Share Price */}
          <div>
            <label className="block text-sm font-semibold text-theme-secondary mb-1.5">
              Current Share Price ($)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-secondary">$</span>
              <input
                type="number"
                name="initialSharePrice"
                value={parameters.initialSharePrice}
                onChange={handleInputChange}
                min="0.01"
                step="0.01"
                className="w-full pl-8 pr-4 py-3 bg-theme-tertiary/50 border border-theme-tertiary rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium transition-all"
              />
            </div>
          </div>

          {/* Time Horizon */}
          <div>
            <div className="flex justify-between mb-1.5">
              <label className="text-sm font-semibold text-theme-secondary">Time Horizon</label>
              <span className="text-sm font-bold text-blue-400">{parameters.timeHorizon} Years</span>
            </div>
            <input
              type="range"
              min="1"
              max="30"
              value={parameters.timeHorizon}
              onChange={(e) =>
                setParameters((prev) => ({
                  ...prev,
                  timeHorizon: parseInt(e.target.value),
                }))
              }
              className="w-full accent-blue-500 h-2 bg-theme-tertiary rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-theme-muted mt-1">
              <span>1 Year</span>
              <span>30 Years</span>
            </div>
          </div>

          {/* Strategy */}
          <div>
            <label className="block text-sm font-semibold text-theme-secondary mb-1.5">
              Investment Strategy
            </label>
            <div className="relative">
              <select
                name="investmentStrategy"
                value={parameters.investmentStrategy}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-theme-tertiary/50 border border-theme-tertiary rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none font-medium cursor-pointer"
              >
                <option value="hold">Buy and Hold</option>
                <option value="sell_reinvest">Sell & Reinvest</option>
                <option value="dollar_cost_averaging">Dollar Cost Averaging</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-secondary pointer-events-none" size={16} />
            </div>
          </div>

          {/* DCA Amount (Conditional) */}
          {parameters.investmentStrategy === 'dollar_cost_averaging' && (
            <div className="animate-fade-in">
              <label className="block text-sm font-semibold text-theme-secondary mb-1.5">
                Weekly Contribution ($)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-secondary">$</span>
                <input
                  type="number"
                  name="weeklyDCAAmount"
                  value={parameters.weeklyDCAAmount}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full pl-8 pr-4 py-3 bg-theme-tertiary/50 border border-theme-tertiary rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                />
              </div>
            </div>
          )}

          {/* Yearly Returns (Simplified Toggle/Accordion could be better, but keeping list for now) */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-theme-secondary">Expected Return</label>
              <div className="text-xs text-theme-muted bg-theme-tertiary px-2 py-1 rounded">Adjust yearly growth</div>
            </div>
            {/* Visual simplification: Just show the first one as "Average" effectively for simple mode if we wanted,
                but keeping the full grid is safer for logic preservation. Let's make it cleaner. */}
            <div className="grid grid-cols-5 gap-1">
              {parameters.yearlyReturns.map((yr) => (
                <div key={yr.year} className="relative group">
                  <div className="h-12 bg-theme-tertiary/30 rounded flex flex-col items-center justify-center border border-transparent hover:border-blue-500/30 transition-all cursor-pointer overflow-hidden">
                    <span className="text-[10px] text-theme-muted">Y{yr.year}</span>
                    <span className="text-xs font-bold text-blue-400">{yr.return}%</span>
                    <input
                      type="range"
                      min="-20"
                      max="50"
                      value={yr.return}
                      onChange={(e) => handleYearlyReturnChange(yr.year, parseInt(e.target.value))}
                      className="absolute inset-0 opacity-0 cursor-ns-resize"
                      title={`Year ${yr.year} Return: ${yr.return}%`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-auto pt-6">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl text-white font-bold text-lg shadow-lg hover:shadow-blue-500/20 transition-all transform hover:-translate-y-0.5 active:translate-y-0 ${isLoading
                ? 'bg-gradient-to-r from-gray-600 to-gray-700 cursor-not-allowed opacity-80'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500'
              }`}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Running...
              </>
            ) : (
              <>
                <Play size={20} fill="currentColor" />
                Run Simulation
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}